import type { ShallowRef } from 'vue'

/**
 * A helper composable function meant to simplify the process of mounting lazy modal components.
 * We often need to have a flag to indicate that the component has been mounted at least once,
 * so that we can use it in a `v-if` directive to avoid re-mounting the component every time it's shown.
 *
 * This composable function returns two reactive flags:
 * - `is` - a flag used for opening / closing the modal
 * - `was` - a flag that indicates whether the component has been mounted at least once
 *
 * @example
 * <template>
 *     <Modal v-if="was" :open="is" />
 *     <button @click="is = !is">Toggle Modal</button>
 * </template>
 *
 * <script lang="ts" setup>
 *     const { is, was } = useLazyMount()
 * </script>
 *
 */
export function useLazyMount(init?: boolean): { is: ShallowRef<boolean>, was: Readonly<Ref<boolean>> }
export function useLazyMount(computed: { get: () => boolean, set: (val: boolean) => void }): { is: ComputedRef<boolean>, was: Readonly<Ref<boolean>> }
export function useLazyMount(arg1?: boolean | { get: () => boolean, set: (val: boolean) => void }) {
    const init = typeof arg1 === 'boolean' ? arg1 : arg1?.get()
    const computedOptions = typeof arg1 === 'object' ? arg1 : undefined

    const is = computedOptions ? computed(computedOptions) : shallowRef<boolean>(init ?? false)
    const was = shallowRef<boolean>(init ?? false)

    if (import.meta.client && !is.value) {
        const unwatch = watch(is, (val) => {
            if (!val) return
            was.value = true
            unwatch()
        }, {
            immediate: true,
        })
    }

    return {
        is: is,
        was: readonly(was),
    }
}

interface UseDelayedBoolOptions {
    /**
     * The delay in milliseconds after which the value will be updated after a change if it has not changed again.
     */
    threshold: number
    /**
     * The minimum time in milliseconds to hold the value for.
     * This means that if the value is set to `true`, it will remain `true` for at least this amount,
     * even if it changes back to `false` before the holding time is over.
     */
    holding: number
}

export function useDelayedBool(value: MaybeRefOrGetter<boolean>, options?: Partial<UseDelayedBoolOptions>): {
    delayed: ShallowRef<boolean>
    reset: () => void
} {
    if (!getCurrentScope()) throw new Error('[useDelayedBool] must be called within a Vue setup context')

    const delayed = shallowRef<boolean>(toValue(value))

    let timeoutSet: NodeJS.Timeout | undefined = undefined
    let timeoutReset: NodeJS.Timeout | undefined = undefined

    watch(() => toValue(value), (newVal, oldVal) => {
        clearTimeout(timeoutSet)

        // leading edge
        if (newVal && !oldVal) {
            clearTimeout(timeoutReset)

            const resetHandler = () => {
                timeoutReset = undefined
                const currentVal = toValue(value)
                if (delayed.value !== currentVal) {
                    delayed.value = currentVal
                }
            }

            const setHandler = () => {
                delayed.value = true
                timeoutSet = undefined

                if (options?.holding === 0) {
                    resetHandler()
                } else {
                    timeoutReset = setTimeout(resetHandler, options?.holding ?? 400)
                }
            }
            if (options?.threshold === 0) {
                setHandler()
            } else {
                timeoutSet = setTimeout(setHandler, options?.threshold ?? 100)
            }
        }
        // trailing edge
        else if (!newVal && oldVal) {
            // if the reset timeout already expired, we can set the value immediately
            if (!timeoutReset) {
                delayed.value = false
            }
        }
    })

    onScopeDispose(() => {
        if (timeoutSet) {
            clearTimeout(timeoutSet)
            timeoutSet = undefined
        }
        if (timeoutReset) {
            clearTimeout(timeoutReset)
            timeoutReset = undefined
        }
    })

    function reset() {
        clearTimeout(timeoutSet)
        clearTimeout(timeoutReset)
        timeoutSet = undefined
        timeoutReset = undefined
        delayed.value = toValue(value)
    }

    return {
        delayed,
        reset,
    }
}
